import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Users, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { userAPI, chatAPI } from '@/lib/api';
import { getInitials } from '@/lib/utils';
import { toast } from 'sonner';

export const NewChatDialog = ({ open, onOpenChange, type, currentUser, onChatCreated }) => {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const isGroup = type === 'group';

  // Load users
  useEffect(() => {
    const loadUsers = async () => {
      setLoading(true);
      try {
        const response = await userAPI.getUsers(search);
        setUsers(response.data);
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      loadUsers();
    }
  }, [open, search]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedUsers([]);
      setGroupName('');
    }
  }, [open]);

  const handleUserSelect = (user) => {
    if (isGroup) {
      setSelectedUsers(prev => {
        const exists = prev.find(u => u.id === user.id);
        if (exists) {
          return prev.filter(u => u.id !== user.id);
        }
        return [...prev, user];
      });
    } else {
      // For single chat, create immediately
      handleCreateChat([user]);
    }
  };

  const handleCreateChat = async (participants = selectedUsers) => {
    if (isGroup && participants.length < 2) {
      toast.error('Eine Gruppe braucht mindestens 2 Teilnehmer');
      return;
    }

    if (isGroup && !groupName.trim()) {
      toast.error('Bitte einen Gruppennamen eingeben');
      return;
    }

    setCreating(true);
    try {
      const response = await chatAPI.createChat({
        participant_ids: participants.map(u => u.id),
        is_group: isGroup,
        name: isGroup ? groupName.trim() : undefined
      });
      
      onChatCreated(response.data);
      onOpenChange(false);
      toast.success(isGroup ? 'Gruppe erstellt' : 'Chat gestartet');
    } catch (error) {
      toast.error('Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const isSelected = (user) => selectedUsers.some(u => u.id === user.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isGroup ? 'Neue Gruppe erstellen' : 'Neuer Chat'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Group name input */}
          {isGroup && (
            <div className="space-y-2">
              <Label>Gruppenname</Label>
              <Input
                placeholder="Name der Gruppe..."
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="h-10 rounded-xl"
                data-testid="group-name-input"
              />
            </div>
          )}

          {/* Selected users for group */}
          {isGroup && selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map(user => (
                <motion.div
                  key={user.id}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="flex items-center gap-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-2 py-1 rounded-full text-sm"
                >
                  {user.username}
                  <button
                    onClick={() => handleUserSelect(user)}
                    className="hover:bg-emerald-200 dark:hover:bg-emerald-800 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </motion.div>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Benutzer suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 rounded-xl"
              data-testid="search-users-input"
            />
          </div>

          {/* Users list */}
          <ScrollArea className="h-[300px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                Keine Benutzer gefunden
              </div>
            ) : (
              <div className="space-y-1">
                {users.map(user => (
                  <motion.div
                    key={user.id}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => handleUserSelect(user)}
                    className={`
                      flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors
                      ${isSelected(user) 
                        ? 'bg-emerald-50 dark:bg-emerald-900/20' 
                        : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                      }
                    `}
                    data-testid={`user-item-${user.id}`}
                  >
                    <div className="relative">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.avatar} />
                        <AvatarFallback className="bg-slate-200 dark:bg-slate-700">
                          {getInitials(user.username)}
                        </AvatarFallback>
                      </Avatar>
                      {user.is_online && (
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white dark:border-slate-900 rounded-full" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate">
                        {user.username}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                        {user.email}
                      </p>
                    </div>

                    {isGroup && isSelected(user) && (
                      <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Create group button */}
          {isGroup && (
            <Button
              onClick={() => handleCreateChat()}
              disabled={creating || selectedUsers.length < 2 || !groupName.trim()}
              className="w-full h-11 rounded-xl bg-emerald-500 hover:bg-emerald-600"
              data-testid="create-group-btn"
            >
              {creating ? (
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>
                  <Users className="w-4 h-4 mr-2" />
                  Gruppe erstellen ({selectedUsers.length} Teilnehmer)
                </>
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewChatDialog;
